import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { Popover, Transition, PopoverButton, PopoverPanel } from '@headlessui/react';
import { useCookies } from 'next-client-cookies';
import { CheckIcon, DownArrow } from 'components/icons';
import { cleanString } from 'helpers/utils/util';
import { getLocalizationInfo } from 'project.config';
import { useProductList } from '../../context';
import styles from '../../styles/index.module.css';
import { SortConfiguration } from '../../types';

export interface SortFacetProps {
  sortConfiguration?: SortConfiguration[];
  setIsOpen?: Dispatch<SetStateAction<boolean>>;
}

const SortFacet: React.FC<SortFacetProps> = ({ sortConfiguration, setIsOpen }) => {
  const { productTitle, sortBy, newest, relevance, pricehtl, pricelth } = sortConfiguration?.[0] ?? {};

  const cookies = useCookies();
  const locale = cookies.get('locale') as string;

  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const { replaceSort, activeSort } = useProductList();

  const [liveAnnouncement, setLiveAnnouncement] = useState('');

  const currency = getLocalizationInfo(locale).currency;

  const options = useMemo<
    Array<{
      label: string | undefined;
      attribute: string;
      value: 'asc' | 'desc';
    }>
  >(
    () => [
      {
        label: `${productTitle} - A-Z`,
        attribute: 'name',
        sortAttributes: [{ 'variants.attributes.design_name_universal': 'asc' }],
        value: 'asc',
      },
      {
        label: `${productTitle} - Z-A`,
        attribute: 'name',
        sortAttributes: [{ 'variants.attributes.design_name_universal': 'asc' }],
        value: 'desc',
      },
      {
        label: relevance,
        attribute: '',
        value: 'desc',
      },
      {
        label: pricehtl,
        attribute: 'price',
        filterBy: currency,
        sortAttributes: [{ 'prices.value.centAmount': 'desc' }],
        currency: '',
        value: 'desc',
      },
      {
        label: pricelth,
        attribute: 'price',
        filterBy: currency,
        sortAttributes: [{ 'prices.value.centAmount': 'asc' }],
        currency: '',
        value: 'asc',
      },
      {
        label: newest,
        attribute: 'createdAt',
        value: 'desc',
      },
    ],
    [productTitle, relevance, pricehtl, pricelth, newest, currency],
  );

  const selectedOption = options.find(
    (option) => option.attribute === activeSort?.attribute && option.value === activeSort?.value,
  );

  const handleSortOptionClick = (attribute: string, value: 'asc' | 'desc', label?: string) => {
    replaceSort({ attribute, value });

    setLiveAnnouncement(label ? `Sort order changed to ${label}` : '');

    const safeSetIsOpen = setIsOpen ?? (() => {});
    safeSetIsOpen(false);

    setTimeout(() => {
      triggerRef.current?.focus();
    }, 0);
  };

  return (
    <>
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {liveAnnouncement}
      </div>

      <Popover
        className="sortDropdownContainer relative reg:w-3/4 lg:w-auto"
        data-testid="btn-sort-products-options"
        data-ffid="cbo-sort-products"
      >
        {({ open }) => (
          <>
            <PopoverButton
              ref={triggerRef}
              aria-haspopup="listbox"
              name="Sort Option"
              aria-expanded={open}
              aria-controls="sort-options-listbox"
              aria-label={`Sort by ${selectedOption?.label ?? ''}`}
              data-testid="btn-sort-products-options"
              data-ffid="btn-sort-products-options"
              className={`w-full items-center rounded-md border bg-white ${
                open ? styles.facet_dropdown_selected : styles.facet_dropdown_unselected
              } py-8 pl-12 pr-40 dark:bg-[#1c1b1f]`}
            >
              <div className="relative flex w-full items-start lg:min-w-240" data-ffid="ctr-sort-products-options">
                <span className="mr-3 text-16 font-normal text-black dark:text-white" data-ffid="lbl-sort-by">
                  {sortBy}
                </span>

                <span className="text-16 font-normal text-[#5C5C5C] dark:text-white" data-ffid="lbl-sort-by-text">
                  {selectedOption?.label}
                </span>

                <span className="absolute -right-40 top-0" aria-hidden="true">
                  <DownArrow className="w-40 text-[#333333] dark:text-white" data-ffid="icon-sort-down-arrow" />
                </span>
              </div>
            </PopoverButton>

            <Transition
              enter="transition ease-out duration-100"
              enterFrom="transform origin-top scale-y-0"
              enterTo="transform origin-top scale-y-100"
              leave="transition ease-in duration-100"
              leaveFrom="transform origin-top scale-y-150"
              leaveTo="transform origin-top scale-y-0"
              data-ffid="ctr-sort-popover-panel-parent"
            >
              <PopoverPanel
                id="sort-options-listbox"
                role="listbox"
                aria-label="Sort options"
                className="absolute left-0 top-56 z-[300] w-full rounded-md rounded-b-sm border border-[#d6d6d6] bg-white py-20"
                data-ffid="ctr-sort-popover-panel"
              >
                {options.map(({ label, attribute, value }, index) => {
                  const isSelected = attribute === activeSort?.attribute && value === activeSort?.value;

                  return (
                    <PopoverButton
                      key={`${label}-${index}`}
                      role="option"
                      aria-selected={isSelected}
                      aria-label={`${label} option ${isSelected ? 'selected' : 'not selected'}`}
                      data-testid="btn-sort-products-option"
                      data-ffid={`btn-sort-products-option-${index}`}
                      className={`${
                        isSelected ? 'pl-12' : ''
                      } flex w-full items-center justify-start gap-8 px-0 py-8 text-left transition hover:bg-neutral-200 dark:bg-[#1c1b1f]`}
                      onClick={() => handleSortOptionClick(attribute, value, label)}
                    >
                      {isSelected && <CheckIcon aria-hidden="true" className="size-20 text-gray-700" />}

                      <span
                        className={`text-16 leading-5 text-gray-700 dark:text-white ${isSelected ? 'pl-0' : 'pl-40'}`}
                        data-ffid={`lbl-sort-products-option-${cleanString(label ?? '')}-${index}`}
                      >
                        {label}
                      </span>
                    </PopoverButton>
                  );
                })}
              </PopoverPanel>
            </Transition>
          </>
        )}
      </Popover>
    </>
  );
};

export default SortFacet;
